<template>
	<article class="category">
		<banner
			v-if="bannerAtTop && banner"
			:banner="banner"
			:analyticsData="analyticsData"
		/>
		<div v-else-if="mq.current >= mq.lg" class="p-4" />
		<template v-if="category">
			<div class="category-header">
				<ximg
					:src="iconImage"
					:ariaLabel="category.icon"
					role="img"
				/>
				<h1>
					{{ category.seoName || category.name }}
				</h1>
			</div>
			<!-- @TODO fix relative links -->
			<a @click="openHamburgerMenu" class="ml-5 hidden-lg underlined-link">{{ categoryLink }}</a>
			<html-description class="p-5 page-description" :html="category.description" />
			<div v-if="firstTwoArticles.length" class="article-link-wrap">
				<template v-for="article in firstTwoArticles">
					<router-link
						v-if="article"
						:key="article.id"
						:to="formatArticleRoute(article)"
						:class="['info-link', {'one-article': firstTwoArticles.length === 1}]"
					>
						<img
							:src="articleImage(article.image)"
							:alt="'artikel ' + article.heading"
						>

						<ximg
							:class="{ dark: isDark }"
							:src="articleIcon(article.icon)"
							:ariaLabel="category.icon"
							role="img"
						/>
						<span class="article-info">
							<span class="heading">
								{{ article.heading }}
							</span>
							<span class="sub-title">
								{{ article.subheading }}
							</span>
						</span>
					</router-link>
				</template>
			</div>
			<div class="clearfix"></div>
			<html-description class="p-5 page-description" :extended-html="category.extendedDescription" :afterExtendedHtml="afterExtendedHtml" />
		</template>
		<product-browser
			v-if="category"
			ref="browser"
			:category="category"
			:banner="bannerAtTop ? null : banner"
			:canSetPageQuery="browserCanSetPage"
			:canSetCanonical="false"
			:url="'category/' + category.id"
			:resetData="resetData"
			:sortFromSettings="sortFromSettings"
			@loaded="loaded"
		/>
	</article>
</template>

<script>
import Vue from 'vue';
import { isEqual } from 'lodash';
import waja from 'waja';
import bus from 'eventbus';
import router from 'router';
import mq from 'mediaQuery';
import store from 'datastore';
import tagHandler from 'tagHandler';
import metaHandler from 'metaHandler';
import { sectionIds } from 'staticData';
import { mapGetters, mapState } from 'vuex';
import { error, convertToCdnUrl, findCurrentPath, current, urlFormat, standardRoute } from 'utils';
import { updateCategoryHighlights } from 'utils/highlight';
import { setSectionAndCategories } from 'datastore/navStatus';
import { trackImpression } from 'gtm';

export default {
	store,
	name: 'pageCategory',
	layoutOptions: {
		leftCol: true,
	},
	data () {
		return {
			mq,
			error,
			convertToCdnUrl,
			urlFormat,
			category: null,
			banner: null,
			productsLoaded: false,
			topListsLoaded: false,
			/*
			 * Controls whether the product browser can update the page or not (url query).
			 * This is disabled when the hamburger menu is open to prevent it from being
			 * closed when the user is scrolling in the menu (when the product browser
			 * sets the page in the background which closes the menu).
			 */
			browserCanSetPage: true,
			// Helps set a dynamic "show more categories" link
			currentPath: this.findCurrentPath(),
			hasSubCategories: null,
			categoryLink: null,
			firstTwoArticles: [],
			restOfArticles: [],
			resetData: [],
			sortFromSettings: null,
		};
	},
	// @TODO kanske ska flytta dessa värden till ett objekt, ex applicationOptions
	willSendScrollTriggerEvent: true,
	handlesMeta: true,
	handleTakeovers: true,
	handlesPrerender: true,
	handleNavigation () {
		return true;
	},
	beforeRouteEnter (to, frm, next) {
		const id = to.params.id;
		waja.get('category/' + id + '/' + store.getters['i18n/regionCode'])
			.useCdn(true)
			.on('success', res => {
				next(vm => {
					vm.productsLoaded = false;
					vm.topListsLoaded = false;
					vm.category = res.data.category;
					vm.firstTwoArticles = res.data.category.articles.slice(0,2);
					vm.restOfArticles = res.data.category.articles.slice(2, res.data.category.articles.length);
				});
			})
			.on('error', res => {
				if (res.status === 404) {
					res.json().then(json => {
						let data;
						if (json instanceof Object) {
							data = json;
						}
					next(error(404, 'Kategorin kunde inte hittas', data));
					});
				} else {
					next(error(500, 'Det går för tillfället inte att komma åt denna sida. Försök gärna senare.'));
				}
			}).go();
	},
	beforeRouteUpdate (to, frm, next) {
		// Id params seem to be strings sometimes and numbers other times.
		const id = String(to.params.id);
		const frmId = String(frm.params.id);

		if (to.name === frm.name && id === frmId) {
			next(vm => {
				// Check if url query has changed.
				if (! isEqual(to.query, frm.query)) {
					// Update SEO.
					vm.updateSEO(vm.category);
					// Prerender init.
					vm.productsLoaded = false;
				} else {
					// Page is already loaded.
					vm.onPageLoad();
				}
			});
			return;
		}

		// Load new category.
		waja.get('category/' + id + '/' + store.getters['i18n/regionCode'])
			.useCdn(true)
			.on('success', res => {
				next(vm => {
					vm.productsLoaded = false;
					vm.topListsLoaded = false;
					vm.category = res.data.category;
					vm.firstTwoArticles = res.data.category.articles.slice(0,2);
					vm.restOfArticles = res.data.category.articles.slice(2, res.data.category.articles.length);
				});
			})
			.on('error', res => {
				res.json().then(json => {
					let data;
					if (json.length) {
						data = json;
					}
					next(error(404, 'Kategorin kunde inte hittas', data));
				});
			})
			.go();
	},
	methods: {
		articleImage(image) {
			return convertToCdnUrl(image);
		},
		articleIcon(icon) {
			return convertToCdnUrl('/api/dynimg/category/'+ icon + (this.isDark ? '/39485D' : '/333333'));
		},
		openHamburgerMenu () {
			// Disable product browser from updating page while burger is open,
			// to prevent it from being closed while scrolling the menu.
			this.browserCanSetPage = false;
			store.commit('navStatus/burgerOpen', true);
		},
		onProductsLoad (products = null, page = null, productsPerPage = 12) {
			this.productsLoaded = true;
			this.onPageLoad();

			if(products && page) {
				const extraData = {
					stock: true,
					rating: true,
				};
				trackImpression(products, 'Category Page', extraData, { productsPerPage: productsPerPage, page: page });
			}
		},
		onTopListsLoad () {
			this.topListsLoaded = true;
			this.onPageLoad();
		},
		onPageLoad () {
			if (this.productsLoaded && this.topListsLoaded) {
				Vue.nextTick(() => {
					store.dispatch('prerender/pageLoaded');
				});
			}

		},
		updateSEO (to) {
			// Initialize SEO for the category.
			const metaDescription = to.metaDescription || 'Stort utbud av ' + to.name.toLowerCase() +
				' från marknadens största tillverkare. ✓ Låga priser ✓ Öppet köp 30' +
				' dagar ✓ Fri frakt för medlemmar - Köp online eller i butik!';

			metaHandler.set('description', metaDescription);

			if (to.section.id === sectionIds.gaming) {
				metaHandler.set('robots', 'noindex, follow');
			}

			/*
			* Canonical link
			* ------------------------------------------------------------------
			*/
			let canonicalRoute = null;
			if (to.canonicalCategory) {
				canonicalRoute = router.route({
					name: 'category',
					params: {
						id: to.canonicalCategory.id,
						name: to.canonicalCategory.name,
					},
				});
				if (to.canonicalCategory.id !== to.id) {
					// All categories with a canonical link will have noindex as well.
					metaHandler.set('robots', 'noindex, follow');
				}
			} else {
				const route = {
					name: 'category',
					params: {
						id: to.id,
						name: to.name,
					},
				};
				const page = this.$route.query.page || 1;
				if (page > 1) {
					route.query = { page };
				}
				canonicalRoute = router.route(route);
			}
			tagHandler.set({
				id: 'canonical',
				type: 'link',
				attributes: {
					rel: 'canonical',
					href: router.routeToUrl(canonicalRoute),
				},
			});
		},
		findCurrentPath () {
			return findCurrentPath();
		},
		loaded (items) {
			if (items?.products) {
				this.onProductsLoad(items.products, items.page, items.productsPerPage);
			}
		},
		formatArticleRoute (article) {
			return this.$router.route(standardRoute('article', { name: article.heading, ...article }));
		},
		applyFulhax (category) {
			if (category.id === 3199) { // LEGO Ninjago
				const script = document.createElement('script');
				script.setAttribute('src', 'https://files.cdn.leadfamly.com/campaign/sdk/utilities.responsive_iframe.min.js');
				script.onload = () => {
					script.parentNode.removeChild(script);
				};
				document.head.appendChild(script);
			}
		},
	},
	mounted () {
		bus.$on('productTips:rendered', this.onTopListsLoad);
	},
	beforeDestroy () {
		bus.$off('productTips:rendered', this.onTopListsLoad);
	},
	computed: {
		afterExtendedHtml() {
			let html = '';
			this.restOfArticles.forEach(article => {
				let articleLink = '/article/' + article.id;
				let articleHtml = '<a href="' + articleLink +'"style="height:auto; padding-right:0px;">' + article.heading + '</a><br>';
				html = html ? html +  articleHtml : '' +  articleHtml;
			});

			if (html.length > 0) {
				html = html + '<br>';
			}

			return html;
		},
		current () {
			return current(this.currentPath);
		},
		bannerAtTop () {
			return this.mq.current >= mq.md;
		},
		iconImage () {
			if (this.category && this.category.section.color) {
				let color = '1A1A1D';
				return convertToCdnUrl('/api/dynimg/category/' + this.category.icon + '/' + color);
			}
		},
		analyticsData () {
			let type = '';
			let data = {};

			if (this.bannerAtTop && this.banner) {
				if (this.banner.product) {
					type = 'product';
				}
				if (this.banner.campaign) {
					type = 'campaign';
				}
				if (this.banner.category) {
					type = 'category';
				}
				if (this.banner.manufacturer) {
					type = 'manufacturer';
				}
				if (this.banner.info) {
					type = 'info';
				}

				if (type.length) {
					data = {
						id: this.banner[type].id,
						name: this.banner[type].name,
						type: type,
						location: this.$route.name === 'index' ? 'startpage' : this.$route.name,
						index: 0,
					};
				}
			}
			return data;
		},
		...mapState({
			burgerOpen: state => state.navStatus.menu.burgerOpen,
			navStatus: state => state.navStatus,
		}),
		...mapGetters({
			isLoggedIn: 'user/isLoggedIn',
			isDark: 'user/isDark',
		}),
	},
	watch: {
		category (to) {
			router.updateParams({
				id: to.id,
				name: urlFormat(to.name),
			});

			this.updateSEO(to);

			const section = to.section;
			setSectionAndCategories(to.section, to.path);
			if (store.getters['productTips/isTopListLoaded'](section)) {
				this.onTopListsLoad();
			}
			store.commit('productTips/setShowTopLists', true);
			// store.dispatch('productTips/fetchTopLists', { section }); * triggers toplist request
			store.commit('productTips/setShowLists', false);

			updateCategoryHighlights(to.id, banner => {
				this.banner = banner;
			});

			if (this.category.options?.defaultSort) {
				this.sortFromSettings = this.category.options?.defaultSort[0];
			} else {
				this.sortFromSettings = null;
			}

			this.resetData = [
				[{
					type: 'category',
					value: this.category.id,
				}],
				true, // parseUrlQuery
				! this.category.options?.defaultSort, // resetSort
			];
			Vue.nextTick(() => {
				this.applyFulhax(this.category);
			});
			if (! to.totalProductCount) {
				Vue.nextTick(() => {
					this.applyFulhax(this.category);
					this.onProductsLoad();
				});
			}
		},
		burgerOpen (isOpen) {
			if (! isOpen) {
				setTimeout(() => {
					// Allow the browser to update the page in the url query once again.
					this.browserCanSetPage = true;
				}, 100);
			}
		},
		navStatus: {
			handler (to, frm) {
				// Recompute current path as the nav status might not have been
				// loaded correctly when the burger was mounted.
				if (to) {
					this.currentPath = this.findCurrentPath();
				}
			},
			deep: true,
		},
		current (to) {
			if(to.next && this.category) {
				const currentCategory = to.next.find( item => {
					return item.id === this.category.id;
				}) || to;

				let link = '';
				if (currentCategory.hasNext) {
					link = 'Visa underkategorier';
				} else {
					link = 'Visa fler kategorier i ' + to.title;
				}
				this.categoryLink = link;
			}
		},
	},
};
</script>
